import { Button, Input, Label, cn, copyText } from '@dawn/ui';
import { Check, ChevronDown, Copy } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { API_URL } from '@/lib/api';

const COPY_RESET_MS = 2000;

function inlineCode(text: string): ReactNode {
  return (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">{text}</code>
  );
}

/**
 * Copy-to-clipboard affordance reused for the URL field and each code block.
 * Optimistic "Copied" state that resets after a beat, through the
 * execCommand-friendly `copyText` helper so it works on the non-secure local
 * dev origin (dawn.lvh.me).
 */
function CopyButton({
  value,
  label,
  iconOnly = false,
  testId,
}: {
  value: string;
  label: string;
  iconOnly?: boolean;
  testId: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyText(value);
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), COPY_RESET_MS);
  };

  const Icon = copied ? Check : Copy;

  return (
    <Button
      type="button"
      variant="outline"
      size={iconOnly ? 'icon' : 'sm'}
      onClick={() => void handleCopy()}
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      data-testid={testId}
      className={cn(
        'shrink-0 transition-colors',
        !iconOnly && 'gap-1.5',
        copied && 'border-emerald-600 text-emerald-700'
      )}
    >
      <Icon className="h-4 w-4" />
      {!iconOnly && (copied ? 'Copied' : 'Copy')}
    </Button>
  );
}

/** Labelled, horizontally-scrollable code block with a corner copy button. */
function CodeBlock({
  label,
  description,
  code,
  copyLabel,
  testId,
}: {
  label: string;
  description: ReactNode;
  code: string;
  copyLabel: string;
  testId: string;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-dawn-black">{label}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
      <div className="relative">
        <pre className="overflow-x-auto rounded-md bg-muted p-3 pr-12 font-mono text-xs leading-relaxed text-foreground">
          <code>{code}</code>
        </pre>
        <div className="absolute right-2 top-2">
          <CopyButton value={code} label={copyLabel} iconOnly testId={testId} />
        </div>
      </div>
    </div>
  );
}

/**
 * Collapsible connection help, rendered at the foot of Settings → MCP →
 * Connected apps. Collapsed by default behind a "How to connect" toggle so it
 * never crowds the list. The endpoint URL is derived from the current origin +
 * the shared API base, so it always reflects the tenant's real subdomain. No
 * credential appears anywhere: clients sign in with OAuth (TDEV-1199) and open
 * the browser for consent the first time they connect.
 */
export function McpConnectionInstructions() {
  const [open, setOpen] = useState(false);
  const contentId = useId();

  const mcpUrl = `${window.location.origin}${API_URL}/mcp`;

  // OAuth (TDEV-1199): no secret in the command — the client opens the browser
  // for consent the first time it connects.
  const claudeCodeSnippet = `claude mcp add --transport http dawn "${mcpUrl}"`;

  return (
    <div className="mt-4 border-t pt-4">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={contentId}
        data-testid="mcp-connection-toggle"
        className="flex w-full items-center gap-1.5 text-sm font-medium text-dawn-black transition-colors hover:text-dawn-orange"
      >
        <ChevronDown
          className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')}
          aria-hidden
        />
        How to connect a client
      </button>

      {open && (
        <div id={contentId} className="mt-4 space-y-5" data-testid="mcp-connection-instructions">
          <p className="text-sm text-muted-foreground">
            Point an MCP client at the server below. The first time it connects, it opens your
            browser so you can sign in to Dawn and allow access — no key to copy.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="mcp-server-url">Server URL</Label>
            <div className="flex items-center gap-2">
              <Input
                id="mcp-server-url"
                readOnly
                value={mcpUrl}
                className="bg-muted font-mono text-xs text-foreground"
                onFocus={(e) => e.currentTarget.select()}
                data-testid="mcp-server-url"
              />
              <CopyButton value={mcpUrl} label="server URL" testId="mcp-copy-url" />
            </div>
          </div>

          <CodeBlock
            label="Claude Code"
            description={
              <>
                Run this in your terminal, then run {inlineCode('/mcp')} in Claude Code and choose{' '}
                <span className="font-medium">Authenticate</span>:
              </>
            }
            code={claudeCodeSnippet}
            copyLabel="Claude Code command"
            testId="mcp-copy-claude-code"
          />

          <div className="space-y-1.5" data-testid="mcp-connector-instructions">
            <p className="text-sm font-medium text-dawn-black">Claude.ai and Claude Desktop</p>
            <p className="text-sm text-muted-foreground">
              Customize → Connectors → Add → Add custom connector, paste the server URL above, then
              choose Connect. On a Team or Enterprise plan, an owner of your Claude organization
              adds it once for everyone.
            </p>
          </div>

          <div className="space-y-1.5" data-testid="mcp-chatgpt-instructions">
            <p className="text-sm font-medium text-dawn-black">ChatGPT</p>
            <p className="text-sm text-muted-foreground">
              Plugins → + → Add custom MCP server, paste the server URL above, choose OAuth for
              authentication, then Create as a plugin. On a Business or Enterprise workspace, an
              admin may need to allow custom MCP servers first.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
